import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GameDetails as GameDetailsType } from '../types';
import { gameService } from '../services/api';
import GameDetails from '../components/GameDetails';
import { GameDetailsSkeleton } from '../components/skeletons';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const GameDetailsPage: React.FC = () => {
  const { gamePath } = useParams<{ gamePath: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Scroll smooth vers le haut quand on arrive sur la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchGameDetails = async () => {
      if (!gamePath) {
        setError('Chemin du jeu non trouvé');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const gameDetails = await gameService.getGameDetails(gamePath);
        setGame(gameDetails);
      } catch (err) {
        setError('Erreur lors du chargement des détails du jeu');
        console.error('Error fetching game details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
  }, [gamePath]);

  if (loading) {
    return <GameDetailsSkeleton />;
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Erreur
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'Jeu non trouvé'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header with back button - Fixed */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white dark:bg-dark-800 shadow-sm border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </button>
        </div>
      </div>

      {/* Game Details */}
      <div className="pt-32">
        <GameDetails game={game} />
      </div>
    </div>
  );
};

export default GameDetailsPage;
